import { useState, useCallback, useEffect, useMemo } from "react"
import {
  getQuotesByUser,
  createQuote,
  updateQuote,
  deleteQuoteById,
} from "helpers/api_helper"
import { isB2BUser } from "../../../constants/permissions"

/**
 * Quote History Hook
 *
 * Manages save/load/clone/delete lifecycle for quote compositions.
 * Serializes V5 config + cart to/from the backend `quotes` table.
 */
export const useQuoteHistory = ({ v5, storeLogic }) => {
  const [quotes, setQuotes] = useState([])
  const [activeQuoteId, setActiveQuoteId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)

  // Get current user
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("authUser")) || {}
    } catch {
      return {}
    }
  }, [])

  const userId = currentUser.id
  const permissions = currentUser.permissions || {}
  const customerId = currentUser.customer_id
  const isB2B = isB2BUser(permissions)

  // ── Load all quotes for this user ──
  const loadQuotes = useCallback(async () => {
    if (!userId) return
    setLoading(true)
    try {
      const data = await getQuotesByUser(userId)
      // B2B users: only show quotes linked to their customer_id
      if (isB2B && customerId) {
        setQuotes((data || []).filter(q => q.customer_id === customerId))
      } else {
        setQuotes(data)
      }
    } catch (err) {
      console.error("[QuoteHistory] Failed to load quotes:", err)
    } finally {
      setLoading(false)
    }
  }, [userId, isB2B, customerId])

  // Load quotes on mount
  useEffect(() => {
    loadQuotes()
  }, [loadQuotes])

  // ── Collect current state into a saveable object ──
  const collectState = useCallback(() => {
    const config = { ...v5.getConfigSnapshot(), profitMargin: storeLogic.profitMargin }
    const cart = storeLogic.cart
    const itemCount = storeLogic.cartItemCount
    const totalPrice = storeLogic.cartTotal
    return { config, cart, item_count: itemCount, total_price: totalPrice }
  }, [v5, storeLogic])

  // ── Save current quote (update existing or create new) ──
  const saveQuote = useCallback(async (title, customerName = "", customerPhone = "", customerEmail = "", customerAddress = "", customerId = null) => {
    if (!userId) return null
    setSaving(true)
    try {
      const state = collectState()
      const customerFields = {}
      if (customerName) customerFields.customer_name = customerName
      if (customerPhone) customerFields.customer_phone = customerPhone
      if (customerEmail) customerFields.customer_email = customerEmail
      if (customerAddress) customerFields.customer_address = customerAddress
      if (customerId) customerFields.customer_id = customerId

      if (activeQuoteId) {
        // Update existing
        const updated = await updateQuote({
          id: activeQuoteId,
          title: title || undefined,
          ...customerFields,
          config: state.config,
          cart: state.cart,
          item_count: state.item_count,
          total_price: state.total_price,
          status: "draft",
          updated_at: new Date().toISOString(),
        })
        await loadQuotes()
        return updated
      } else {
        // Create new
        const created = await createQuote({
          user_id: userId,
          title: title || "Νέα Σύνθεση",
          customer_name: customerName || "",
          customer_phone: customerPhone || "",
          customer_email: customerEmail || "",
          customer_address: customerAddress || "",
          ...(customerId ? { customer_id: customerId } : {}),
          config: state.config,
          cart: state.cart,
          item_count: state.item_count,
          total_price: state.total_price,
          status: "draft",
        })
        if (created?.id) setActiveQuoteId(created.id)
        await loadQuotes()
        return created
      }
    } catch (err) {
      console.error("[QuoteHistory] Failed to save quote:", err)
      return null
    } finally {
      setSaving(false)
    }
  }, [userId, activeQuoteId, collectState, loadQuotes])

  // ── Save as new (clone/fork) ──
  const saveAsNew = useCallback(async (title, customerName = "") => {
    if (!userId) return null
    setSaving(true)
    try {
      const state = collectState()
      const created = await createQuote({
        user_id: userId,
        title: title || "Αντίγραφο Σύνθεσης",
        customer_name: customerName || "",
        config: state.config,
        cart: state.cart,
        item_count: state.item_count,
        total_price: state.total_price,
        status: "draft",
      })
      if (created?.id) setActiveQuoteId(created.id)
      await loadQuotes()
      return created
    } catch (err) {
      console.error("[QuoteHistory] Failed to clone quote:", err)
      return null
    } finally {
      setSaving(false)
    }
  }, [userId, collectState, loadQuotes])

  // ── Load a specific quote into the editor ──
  const loadQuote = useCallback((quote) => {
    if (!quote) return
    // Restore V5 configuration
    if (quote.config) {
      const cfg = typeof quote.config === "string" ? JSON.parse(quote.config) : quote.config
      v5.restoreConfig(cfg)
      // Restore profit margin if saved in config
      if (cfg.profitMargin != null && storeLogic.setProfitMargin) {
        storeLogic.setProfitMargin(parseFloat(cfg.profitMargin) || 0)
      }
    }
    // Restore cart
    if (quote.cart) {
      const cartData = typeof quote.cart === "string" ? JSON.parse(quote.cart) : quote.cart
      storeLogic.replaceCart(cartData)
    }
    setActiveQuoteId(quote.id)
    // If cart has items, transition to building phase
    const cartData = typeof quote.cart === "string" ? JSON.parse(quote.cart) : (quote.cart || [])
    if (cartData.length > 0) {
      v5.setPhase(1)
    }
    setDrawerOpen(false)
  }, [v5, storeLogic])

  // ── Clone a quote (load its data as a new unsaved composition) ──
  const cloneQuote = useCallback(async (quote) => {
    if (!quote) return
    // Load the data
    if (quote.config) {
      const cfg = typeof quote.config === "string" ? JSON.parse(quote.config) : quote.config
      v5.restoreConfig(cfg)
    }
    if (quote.cart) {
      const cartData = typeof quote.cart === "string" ? JSON.parse(quote.cart) : quote.cart
      storeLogic.replaceCart(cartData)
    }
    // Save as new immediately
    const created = await saveAsNew(`${quote.title || "Σύνθεση"} (αντίγραφο)`, quote.customer_name)
    const cartData = typeof quote.cart === "string" ? JSON.parse(quote.cart) : (quote.cart || [])
    if (cartData.length > 0) {
      v5.setPhase(1)
    }
    setDrawerOpen(false)
    return created
  }, [v5, storeLogic, saveAsNew])

  // ── Delete a quote ──
  const removeQuote = useCallback(async (id) => {
    try {
      await deleteQuoteById(id)
      if (activeQuoteId === id) setActiveQuoteId(null)
      await loadQuotes()
    } catch (err) {
      console.error("[QuoteHistory] Failed to delete quote:", err)
    }
  }, [activeQuoteId, loadQuotes])

  // ── Update status ──
  const setQuoteStatus = useCallback(async (id, status) => {
    try {
      await updateQuote({ id, status, updated_at: new Date().toISOString() })
      await loadQuotes()
    } catch (err) {
      console.error("[QuoteHistory] Failed to update status:", err)
    }
  }, [loadQuotes])

  // ── Start fresh (new quote) ──
  const newQuote = useCallback(() => {
    setActiveQuoteId(null)
    v5.restoreConfig({})
    storeLogic.replaceCart([])
    v5.setPhase(0)
    setDrawerOpen(false)
  }, [v5, storeLogic])

  // Active quote object
  const activeQuote = useMemo(() => {
    return quotes.find(q => q.id === activeQuoteId) || null
  }, [quotes, activeQuoteId])

  return {
    quotes,
    activeQuoteId,
    setActiveQuoteId,
    activeQuote,
    loading,
    saving,
    drawerOpen,
    setDrawerOpen,
    saveDialogOpen,
    setSaveDialogOpen,
    loadQuotes,
    saveQuote,
    saveAsNew,
    loadQuote,
    cloneQuote,
    removeQuote,
    setQuoteStatus,
    newQuote,
  }
}
